import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ExportOptions = ({ 
  leads = [], 
  selectedLeads = [], 
  onExport, 
  onDeploy 
}) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    includeMetrics: true,
    includeValidationDetails: true,
    includeAIScores: true,
    includeSource: false,
    fieldsToInclude: {
      name: true,
      email: true,
      phone: true,
      company: true,
      title: true,
      location: true,
      industry: true,
      validationStatus: true,
      aiConfidence: false,
      tags: false,
      notes: false
    }
  });

  const [deploymentConfig, setDeploymentConfig] = useState({
    targetLists: [],
    createNewList: false,
    newListName: '',
    sendWelcomeEmail: false,
    setAsHighPriority: false
  });

  const handleExportConfigChange = (field, value) => {
    setExportConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldToggle = (field) => {
    setExportConfig(prev => ({
      ...prev,
      fieldsToInclude: {
        ...prev?.fieldsToInclude,
        [field]: !prev?.fieldsToInclude?.[field]
      }
    }));
  };

  const handleDeploymentConfigChange = (field, value) => {
    setDeploymentConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = (format = null) => {
    const exportFormat = format || exportConfig?.format;
    const leadsToExport = selectedLeads?.length > 0 
      ? leads?.filter(lead => selectedLeads?.includes(lead?.id))
      : leads;
    
    onExport?.(leadsToExport, exportFormat, exportConfig?.includeMetrics);
  };

  const handleDeploy = () => {
    const leadsToDeloy = selectedLeads?.length > 0 ? selectedLeads : leads?.map(lead => lead?.id);
    onDeploy?.(leadsToDeloy, deploymentConfig);
  };

  const getExportCount = () => {
    return selectedLeads?.length > 0 ? selectedLeads?.length : leads?.length;
  };

  const exportFormats = [
    { value: 'csv', label: 'CSV', icon: 'FileSpreadsheet' },
    { value: 'xlsx', label: 'Excel', icon: 'FileSpreadsheet' },
    { value: 'json', label: 'JSON', icon: 'FileCode' },
    { value: 'xml', label: 'XML', icon: 'FileCode' }
  ];

  const targetListOptions = [
    { value: 'cold_outreach', label: 'Cold Outreach Campaign' },
    { value: 'warm_leads', label: 'Warm Leads List' },
    { value: 'high_priority', label: 'High Priority Prospects' },
    { value: 'nurturing', label: 'Nurturing Campaign' },
    { value: 'follow_up', label: 'Follow-up List' }
  ];

  return (
    <div className="space-y-6">
      {/* Export Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Export Cleansed Leads
        </h3>
        
        <div className="space-y-4">
          <Select
            label="Export Format"
            value={exportConfig?.format}
            onChange={(value) => handleExportConfigChange('format', value)}
            options={exportFormats}
          />

          {/* Export Options */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Export Options
            </label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeMetrics"
                  checked={exportConfig?.includeMetrics}
                  onChange={(checked) => handleExportConfigChange('includeMetrics', checked)}
                />
                <label htmlFor="includeMetrics" className="text-sm text-foreground cursor-pointer">
                  Include quality assurance reports
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeValidationDetails"
                  checked={exportConfig?.includeValidationDetails}
                  onChange={(checked) => handleExportConfigChange('includeValidationDetails', checked)}
                />
                <label htmlFor="includeValidationDetails" className="text-sm text-foreground cursor-pointer">
                  Include validation details and confidence scores
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeAIScores"
                  checked={exportConfig?.includeAIScores}
                  onChange={(checked) => handleExportConfigChange('includeAIScores', checked)}
                />
                <label htmlFor="includeAIScores" className="text-sm text-foreground cursor-pointer">
                  Include AI confidence scores and tags
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeSource"
                  checked={exportConfig?.includeSource}
                  onChange={(checked) => handleExportConfigChange('includeSource', checked)}
                />
                <label htmlFor="includeSource" className="text-sm text-foreground cursor-pointer">
                  Include data source information
                </label>
              </div>
            </div>
          </div>

          {/* Fields Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Fields to Include
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object?.entries(exportConfig?.fieldsToInclude)?.map(([field, included]) => (
                <div key={field} className="flex items-center space-x-2">
                  <Checkbox
                    id={field}
                    checked={included}
                    onChange={() => handleFieldToggle(field)}
                  />
                  <label htmlFor={field} className="text-sm text-foreground cursor-pointer capitalize">
                    {field?.replace(/([A-Z])/g, ' $1')?.trim()}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex items-center space-x-3 pt-4 border-t border-border">
            <div className="flex-1 text-sm text-muted-foreground">
              {getExportCount()} leads will be exported
            </div>
            <div className="flex items-center space-x-2">
              {exportFormats?.map((format) => (
                <Button
                  key={format?.value}
                  variant={exportConfig?.format === format?.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleExport(format?.value)}
                  disabled={getExportCount() === 0}
                >
                  <Icon name={format?.icon} size={14} className="mr-1" />
                  {format?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Deploy to Lists */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Deploy to Lead Lists
        </h3>
        
        <div className="space-y-4">
          <Select
            label="Target List"
            value={deploymentConfig?.targetLists?.[0] || ''}
            onChange={(value) => handleDeploymentConfigChange('targetLists', [value])}
            options={[
              { value: '', label: 'Select a list...' },
              ...targetListOptions
            ]}
          />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="createNewList"
              checked={deploymentConfig?.createNewList}
              onChange={(checked) => handleDeploymentConfigChange('createNewList', checked)}
            />
            <label htmlFor="createNewList" className="text-sm text-foreground cursor-pointer">
              Create new campaign list
            </label>
          </div>

          {deploymentConfig?.createNewList && (
            <input
              type="text"
              placeholder="New list name..."
              value={deploymentConfig?.newListName}
              onChange={(e) => handleDeploymentConfigChange('newListName', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          )}

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendWelcomeEmail"
                checked={deploymentConfig?.sendWelcomeEmail}
                onChange={(checked) => handleDeploymentConfigChange('sendWelcomeEmail', checked)}
              />
              <label htmlFor="sendWelcomeEmail" className="text-sm text-foreground cursor-pointer">
                Send welcome email sequence
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="setAsHighPriority"
                checked={deploymentConfig?.setAsHighPriority}
                onChange={(checked) => handleDeploymentConfigChange('setAsHighPriority', checked)}
              />
              <label htmlFor="setAsHighPriority" className="text-sm text-foreground cursor-pointer">
                Mark as high-priority leads
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {getExportCount()} leads will be deployed
            </div>
            <Button
              onClick={handleDeploy}
              disabled={getExportCount() === 0 || (!deploymentConfig?.targetLists?.[0] && !deploymentConfig?.createNewList)}
            >
              <Icon name="Send" size={16} className="mr-2" />
              Deploy to Campaign
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Quick Actions
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="flex flex-col items-center p-4 h-auto"
            onClick={() => console.log('Save to CRM')}
            disabled={getExportCount() === 0}
          >
            <Icon name="Database" size={20} className="mb-2" />
            <span className="text-sm">Save to CRM</span>
          </Button>
          
          <Button
            variant="outline"
            className="flex flex-col items-center p-4 h-auto"
            onClick={() => console.log('Create sequence')}
            disabled={getExportCount() === 0}
          >
            <Icon name="Zap" size={20} className="mb-2" />
            <span className="text-sm">Create Sequence</span>
          </Button>
          
          <Button
            variant="outline"
            className="flex flex-col items-center p-4 h-auto"
            onClick={() => console.log('Schedule campaign')}
            disabled={getExportCount() === 0}
          >
            <Icon name="Calendar" size={20} className="mb-2" />
            <span className="text-sm">Schedule Campaign</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;